import { useState, useCallback } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Search, Bell, Wifi, WifiOff } from 'lucide-react'
import { useRealtime, type RealtimeConnectionStatus } from '@dain/automation-data'
import { Sidebar } from './Sidebar'
import { UserMenu } from './UserMenu'
import { cn } from '../../lib/utils'

const pageNames: Record<string, string> = {
  '/': 'Dashboard',
  '/automations': 'Automations',
  '/traces': 'Traces',
  '/errors': 'Errors',
  '/activity': 'Activity Log',
  '/alerts': 'Alerts',
  '/tickets': 'Tickets',
  '/test-cases': 'Test Cases',
  '/test-suites': 'Test Suites',
  '/coverage': 'Coverage',
  '/users': 'Users',
}

function getPageName(pathname: string): string {
  // Check for exact match first
  if (pageNames[pathname]) {
    return pageNames[pathname]
  }

  // Check for partial match (for nested routes like /traces/:id)
  for (const [path, name] of Object.entries(pageNames)) {
    if (path !== '/' && pathname.startsWith(path)) {
      return name
    }
  }

  return 'Observatory'
}

// Connection status indicator component
function ConnectionStatus({ status }: { status: RealtimeConnectionStatus }) {
  const statusConfig = {
    connected: {
      color: 'bg-emerald-500',
      text: 'Live',
      icon: Wifi,
    },
    connecting: {
      color: 'bg-amber-500 animate-pulse',
      text: 'Connecting',
      icon: Wifi,
    },
    disconnected: {
      color: 'bg-slate-400',
      text: 'Offline',
      icon: WifiOff,
    },
    error: {
      color: 'bg-red-500',
      text: 'Error',
      icon: WifiOff,
    },
  }

  const config = statusConfig[status]
  const Icon = config.icon

  return (
    <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
      <div className={cn('h-2 w-2 rounded-full', config.color)} />
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{config.text}</span>
    </div>
  )
}

export function AppShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const location = useLocation()

  // Enable real-time updates - auto-invalidates React Query caches
  const { status: realtimeStatus } = useRealtime({
    enabled: true,
    autoInvalidate: true,
  })

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev)
  }, [])

  const pageName = getPageName(location.pathname)

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      {/* Main Content */}
      <div
        className={cn(
          'transition-all duration-300',
          sidebarCollapsed ? 'ml-16' : 'ml-64'
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
              {pageName}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Real-time Connection Status */}
            <ConnectionStatus status={realtimeStatus} />

            {/* Divider */}
            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search..."
                className="h-9 w-64 rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-blue-500 focus:bg-white dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500 dark:focus:border-blue-500"
              />
            </div>

            {/* Notifications */}
            <button className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300">
              <Bell className="h-5 w-5" />
              <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500" />
            </button>

            {/* User Menu */}
            <UserMenu />
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
